import React, {useEffect, useRef, useState} from "react";
import { createPortal } from "react-dom";
import {
  createStyles,
  Navbar,
  Group,
  UnstyledButton,
  Text,
  Box,
  Collapse,
  Flex,
  TextInput, Tooltip, Indicator
} from "@mantine/core";
import {
  IconChevronUp,
  IconChevronDown,
  IconSearch
} from "@tabler/icons";
import { Link, useLocation } from "react-router-dom";
import { CreateNewModal } from "../../CreateNewModal";
import {MegaPhone, PlusIcon} from "../../../icons/x-symbol-svgrepo-com";
import {useLocalStorage, useClickOutside} from "@mantine/hooks";
import { mainLinks, settingsLinks } from "../menuObj";
import {isBagelAdmin, isBagelEmployee, isBagelLead, getConnectedUserRole} from "../../../utils";
import {useFields} from "../../../hooks/useFields";
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useCurrentUserProfile } from '../../../hooks/useCurrentUserProfile';
import NotificationsDropdown from "./NotificationsDropdown";
import InviteMembersDropdown from "./InviteMembersDropdown";
import Search from "./Search";
import { ExploreBagelSparkleIcon } from "../../explore/ExploreBagelSparkleIcon";
import {
  sidebarFooterTargetClass,
  sidebarFooterLabelClass,
  sidebarNavLinkLabelClass,
  sidebarNavAddTextClass,
} from "./utils";
import ProfileMenu from "./ProfileMenu";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

const useStyles = createStyles((theme) => ({
  box: {
    alignItems: "center",
    marginLeft: "8px",
    marginRight: "8px",
    alignSelf: "stretch",
    fontSize: "14px",
  },

  settingsNavItem: {
    width: "100%",
    minWidth: 0,
    alignSelf: "stretch",
    fontSize: "14px",
  },

  settingsLink: {
    marginTop: 0,
    marginBottom: 0,
  },

  footer: {
    borderTop: `1px solid ${
      theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]
    }`,
  },

  link: {
    marginTop: "2px",
    marginBottom: "2px",
    borderRadius: "8px",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    textDecoration: "none",
    color: "#161832",
    fontWeight: 500,
    lineHeight: "20px",
    minHeight: "36px",
    width: "100%",
    backgroundColor: "transparent",
    transition: "background-color 0.15s ease",
    boxSizing: "border-box",

    "&:hover": {
      backgroundColor: "#F0F0F0",
    },
  },

  linkIcon: {
    color: "#161832",
  },

  /** Keeps nav glyphs (incl. filled SVGs) optically centered in the 36px row */
  navIconSlot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
  },

  linkActive: {
    color: "#5b60e3",
    borderRadius: "8px",
    backgroundColor: "#EAEAFE",
    "&:hover": {
      backgroundColor: "#EAEAFE",
    },
    // linkIcon forces #161832; keep icons aligned with active label (same as Tabler `color` on settings)
    "& $linkIcon": {
      color: "#5b60e3",
    },
    /** Profile menu: row hover uses Mantine gray; keep active tint + brand text */
    "[data-menu-item][data-hovered] &": {
      color: "#5b60e3",
      backgroundColor: "#EAEAFE",
      "& $linkIcon": {
        color: "#5b60e3",
      },
    },
  },

  linkDisabled: {
    cursor: "not-allowed",
    "&:hover": {
      color: "#000",
      "& $linkIcon": {  // Updated syntax to target linkIcon within linkDisabled
        color: "#000",
      },
    },
  },

  menuText: {
    transition: "opacity 0.2s ease",
    opacity: 0,
  },

  submenu: {
    left: "100%",
    top: 0,
    background: "#F8F8FB",
    border: "1px solid #EAEBEE",
    borderRadius: "8px",
    padding: "4px",
    minWidth: "150px",
    overflowY: "auto", // Enable vertical scrolling
    overflowX: "hidden", // Hide horizontal overflow
    display: "none",
    opacity: 0,
    visibility: "hidden",
    transform: "translateX(-10px)",
    transition: "all 0.2s ease",
    zIndex: 999,
    marginLeft: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    scrollbarWidth: "thin", // Firefox - thin scrollbar
    scrollbarColor: "#C1C2C5 transparent", // Firefox - scrollbar colors
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#C1C2C5",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#A6A7AB",
    },
  },

  menuWrapper: {
    "&:hover": {
      "& $menuText": {
        opacity: 1,
        transition: "all 0.5s ease",
      },
      "& $submenu": {
        opacity: 1,
        visibility: "visible",
        transform: "translateX(0)",
        transition: "all 0.5s ease",
      }
    }
  },

  submenuContent: {
    background: "#F8F8FB",
    marginLeft: "24px",
    marginTop: "2px",
    maxHeight: "32vh",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "thin",
    scrollbarColor: "#C1C2C5 transparent",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#C1C2C5",
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#A6A7AB",
    },
  },

  submenuItem: {
    display: "block",
    padding: "8px 16px",
    color: "#161832",
    textDecoration: "none",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "6px",
    
    "&:hover": {
      backgroundColor: "#EAEBEE",
    },
  },

  submenuItemActive: {
    color: "#5C5CEB",
    backgroundColor: "#EAEAFE",
  },

  submenuSearch: {
    marginBottom: "8px",
    "& input": {
      fontSize: "12px",
      padding: "6px 8px",
      borderRadius: "4px",
      border: "1px solid #EAEBEE",
      backgroundColor: "#FFFFFF",
      "&:focus": {
        borderColor: "#5C5CEB",
        outline: "none",
      },
    },
    "& .mantine-TextInput-icon": {
      color: "#ADB5BD",
    },
  },

}));

const NAV_COLLAPSE_DELAY_MS = 300;

export function MainNav() {
  const navRef = useRef<HTMLElement>(null);
  const [hovered, setHovered] = useState(false);
  const navCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearNavCollapseTimer = () => {
    if (navCollapseTimerRef.current != null) {
      clearTimeout(navCollapseTimerRef.current);
      navCollapseTimerRef.current = null;
    }
  };

  useEffect(() => () => clearNavCollapseTimer(), []);

  const handleNavMouseEnter = () => {
    clearNavCollapseTimer();
    setHovered(true);
  };

  const handleNavMouseLeave = () => {
    if (profileMenuOpenedRef.current) return;
    clearNavCollapseTimer();
    navCollapseTimerRef.current = window.setTimeout(() => {
      navCollapseTimerRef.current = null;
      setHovered(false);
    }, NAV_COLLAPSE_DELAY_MS);
  };

  const { pathname } = useLocation();
  const currentUser = useCurrentUser();

  const isSystemAdmin = isBagelAdmin(currentUser);
  const isBagelEmployeeUser = isBagelEmployee(currentUser);
  const isBagelLeadUser = isBagelLead(currentUser);
  const userRole = getConnectedUserRole(currentUser);
  const menuType: boolean = pathname.indexOf("/settings") === -1;
  const { classes, cx } = useStyles();
  const [modalOpened, setModalOpened] = useState({
    idea: false,
  });
  const [openCollapse, setOpenCollapse] = useState({});
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [submenuSearchTerms, setSubmenuSearchTerms] = useState<{[key: string]: string}>({});
  const { threeSixtyEnabled, subPages, showWhatsNewBanner } = useFields();
  const { canCreateIdeas } = useUserPermissions();
  const { canSeeExplore } = useCurrentUserProfile();

  const [isBannerOpened, setIsBannerOpened] = useLocalStorage<boolean | null>({
    key: "isWhatsNewOpened",
    defaultValue: showWhatsNewBanner ? true : false,
  });
  const [isBannerRead, setIsBannerRead] = useLocalStorage<boolean | null>({
    key: "isWhatsNewRead",
    defaultValue: showWhatsNewBanner ? false : true,
  });
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [ideaQuery, setIdeaQuery] = useState('');
  const [searchPopoverOpened, setSearchPopoverOpened] = useState(false);
  const searchPopoverOpenedRef = useRef(false);
  searchPopoverOpenedRef.current = searchPopoverOpened;
  const [profileMenuOpened, setProfileMenuOpened] = useState(false);
  const profileMenuOpenedRef = useRef(false);
  profileMenuOpenedRef.current = profileMenuOpened;

  const searchSpotlightRef = useClickOutside(() => setSearchPopoverOpened(false));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        if (e.repeat) return;
        e.preventDefault();
        setSearchPopoverOpened((open) => !open);
        return;
      }
      if (e.key === "Escape" && searchPopoverOpenedRef.current) {
        e.preventDefault();
        setSearchPopoverOpened(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  useEffect(() => {
    if (!searchPopoverOpened) {
      setIdeaQuery("");
    }
  }, [searchPopoverOpened]);

  useEffect(() => {
    if (!profileMenuOpened && hovered) {
      const navEl = navRef.current;
      const isMouseOutside = navEl && !navEl.matches(":hover");
      if (isMouseOutside) {
        clearNavCollapseTimer();
        navCollapseTimerRef.current = setTimeout(() => {
          navCollapseTimerRef.current = null;
          setHovered(false);
        }, NAV_COLLAPSE_DELAY_MS);
      }
    }
  }, [profileMenuOpened]);

  useEffect(() => {
    setSearchPopoverOpened(false);
  }, [pathname]);

  /** Spotlight is portaled to `body`; keep the rail narrow while it is open so hover on the panel does not expand the sidebar. */
  const navExpandedChrome = (hovered || profileMenuOpened) && !searchPopoverOpened;
  const sidebarFooterExpanded = !menuType || navExpandedChrome;

  const handleCheckOut = (closeTooltip = false) => {
    window.open("https://help.getbagel.com/en/articles/10429772-2025-releases", "_blank");
    setIsBannerRead(true);
    setIsBannerOpened(false);
    if (closeTooltip) {
      setTooltipOpened(false);
    }
  }

  const toggleCollapse = (key: string) => {
    setOpenCollapse((prev: any) => ({ ...prev, [key]: !prev[key] }));
  };

  // Helper function to filter submenu items based on search term
  const filterSubmenuItems = (items: any[], searchTerm: string) => {
    if (!searchTerm) return items;
    return items.filter((item) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Helper function to handle search input changes
  const handleSubmenuSearch = (itemLabel: string, searchTerm: string) => {
    setSubmenuSearchTerms((prev) => ({
      ...prev,
      [itemLabel]: searchTerm,
    }));
  };


  // Get custom children from fields
  const getCustomIdeasChildren = () => {
    if (!subPages?.length) {
      return [];
    }
    return subPages
      .filter((page: any) => page.enabled !== false)
      .sort((a: any, b: any) => (a.index || 0) - (b.index || 0))
      .map((page: any) => ({
        link: `/ideas/${page.route}`,
        label: page.label,
        disabled: false
      }));
  };

  // Update mainLinks with custom children
  const updatedMainLinks = mainLinks.map(link => {
    if (link.label === "Ideas" && link.children) {
      const child = [
        ...getCustomIdeasChildren(),
        link.children[1] // Keep "Archived" as last item
      ]
      return {
        ...link,
        children: child
      };
    }
    return link;
  });

  let links;

  if (!menuType) {

    links = settingsLinks.map((section: any) => {
      const visibleItems = section.items.filter((item: any) => {
        if (item.onlyBagelLead) return isBagelLeadUser;
        if (item.onlyBagelAdmin) return isSystemAdmin;
        if (item.onlyBagel) return isBagelEmployeeUser;
        return true;
      });

      if (visibleItems.length === 0) return null;

      return (
        <Box key={section.section}>
          <Text
            fz="11px"
            fw={600}
            color="dimmed"
            tt="uppercase"
            mt="md"
            mb={4}
            px={8}
            sx={{ letterSpacing: "0.5px" }}
          >
            {section.section}
          </Text>
          {visibleItems.map((item: any) => {
            const selectedItem: boolean = pathname === item.link;
            const hasChildren = item.children && item.children.length > 0;
            const isParentSectionOpen =
              /* @ts-ignore */
              openCollapse[item.label] || pathname.includes(item.link);
            return (
              <Box className={classes.settingsNavItem} key={item.link || item.label}>
                <div style={{ width: "100%" }}>
                  {!hasChildren ? (
                    <Link
                      to={item.link}
                      className={cx(classes.link, classes.settingsLink, {
                        [classes.linkActive]: selectedItem,
                      })}
                    >
                      <Box className={classes.navIconSlot}>
                        {selectedItem ? (
                          <item.activeIcon
                            className={classes.linkIcon}
                            color="#5C5CEA"
                            stroke={1.5}
                          />
                        ) : (
                          <item.icon className={classes.linkIcon} stroke={1.5} />
                        )}
                      </Box>
                      <Text lineClamp={1} ml="md" fz="14px" mb={0}>
                        {item.label}
                      </Text>
                    </Link>
                  ) : (
                    <>
                      <UnstyledButton
                        onClick={() => hasChildren && toggleCollapse(item.label)}
                        w="100%"
                        styles={{ root: { padding: 0 } }}
                      >
                        <Group position="apart" noWrap align="center" spacing={4} w="100%" pr={6}>
                          <Box
                            className={cx(classes.link, classes.settingsLink, {
                              [classes.linkActive]: selectedItem,
                            })}
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Box className={classes.navIconSlot}>
                              {selectedItem ? (
                                <item.activeIcon
                                  className={classes.linkIcon}
                                  stroke={1.5}
                                />
                              ) : (
                                <item.icon className={classes.linkIcon} stroke={1.5} />
                              )}
                            </Box>
                            <Text lineClamp={1} ml="md" fz="14px" mb={0}>
                              {item.label}
                            </Text>
                          </Box>
                          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                            {isParentSectionOpen ? (
                              <IconChevronUp size={16} />
                            ) : (
                              <IconChevronDown size={16} />
                            )}
                          </Box>
                        </Group>
                      </UnstyledButton>

                      <Collapse in={isParentSectionOpen}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          {item.children.map((child: any) => {
                            const selectedChildItem: boolean = pathname === child.link;
                            return (
                              <Link
                                key={child.link || child.label}
                                to={child.link}
                                className={cx(classes.link, classes.settingsLink, {
                                  [classes.linkActive]: selectedChildItem,
                                })}
                              >
                                <Text ml={40} lineClamp={1} fz="14px" mb={0}>
                                  {child.label}
                                </Text>
                              </Link>
                            );
                          })}
                        </Box>
                      </Collapse>
                    </>
                  )}
                </div>
              </Box>
            );
          })}
        </Box>
      );
    }).filter(Boolean);
  } else {
    if (!threeSixtyEnabled) {
      links = updatedMainLinks?.filter((item: any) => item.label !== "Customer 360")
    }
    else if (threeSixtyEnabled) {
      links = updatedMainLinks
    }

    links = links?.filter((item: any) => {
      if (item.requiresCanSeeExplore && !canSeeExplore) {
        return false;
      }
      if (item.hideForExploreUsers && canSeeExplore) {
        return false;
      }
      if (item.onlyBagel && !isBagelEmployeeUser) {
        return false;
      }
      if (item.roles && Array.isArray(item.roles) && item.roles.length > 0) {
        return item.roles.includes(userRole);
      }
      return true;
    });
    
    links = links?.map((item: any) => {
      const selectedItem: boolean =
        (item.link === "/ideas" &&
          !["/evidences/inbox", "/trends",'/discovery', '/customer360', '/archived'].includes(pathname)) ||
        item.link === pathname;
                
      const hasChildren = item.children && item.children.length > 0;
      const isSubmenuOpen = openSubmenu === item.label;

      return (
        <Box
          key={item.link || item.label}
          className={classes.box}
          onMouseEnter={() => {
            if (hasChildren) setOpenSubmenu(item.label);
          }}
          onMouseLeave={() => {
            if (hasChildren) {
              setOpenSubmenu(null);
              setSubmenuSearchTerms((prev) => ({
                ...prev,
                [item.label]: "",
              }));
            }
          }}
        >
          <Flex align="center" justify="space-between" className={cx(classes.link, {
            [classes.linkActive]: selectedItem,
          })}>
            <Link
              key={item.link || item.label}
              to={item.link}
              style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}
            >
              <Flex align="center">
                <Box>
                  {selectedItem ? (
                    <item.activeIcon className={classes.linkIcon} stroke={1.5} />
                  ) : (
                    <item.icon className={classes.linkIcon} stroke={1.5} />
                  )}
                </Box>
                <Text
                  className={sidebarNavLinkLabelClass(navExpandedChrome)}
                  lineClamp={1}
                  ml="md"
                  fz="14px"
                  mb="1px"
                >
                  {item.label}
                </Text>
              </Flex>
            </Link>
            {/*)}*/}
            {hasChildren && (
              <UnstyledButton 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const newSubmenuState = isSubmenuOpen ? null : item.label;
                  setOpenSubmenu(newSubmenuState);
                  // Clear search when closing submenu
                  if (!newSubmenuState) {
                    setSubmenuSearchTerms((prev) => ({
                      ...prev,
                      [item.label]: '',
                    }));
                  }
                }}
                style={{ padding: '4px' }}
              >
                {isSubmenuOpen ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </UnstyledButton>
            )}
          </Flex>


          {hasChildren && (
            <Collapse in={isSubmenuOpen}>
              <div className={classes.submenuContent}>
                {/* Show search input if more than 9 items */}
                {item.children.length > 9 && (
                  <div className={classes.submenuSearch}>
                    <TextInput
                      icon={<IconSearch size={14} />}
                      placeholder="Search subpages"
                      size="xs"
                      value={submenuSearchTerms[item.label] || ''}
                      onChange={(e) => handleSubmenuSearch(item.label, e.currentTarget.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                
                {/* Render filtered items */}
                {(() => {
                  const searchTerm = submenuSearchTerms[item.label] || '';
                  const filteredChildren = filterSubmenuItems(item.children, searchTerm);
                  
                  if (filteredChildren.length === 0 && searchTerm) {
                    return (
                      <Text size="xs" color="dimmed" p="sm" align="center">
                        No matches found
                      </Text>
                    );
                  }
                  
                  return filteredChildren.map((child: any) => (
                    <Link
                      key={child.link || child.label}
                      to={child.link}
                      className={cx(classes.submenuItem, {
                        [classes.linkActive]: pathname === child.link,
                      })}
                    >
                      <Flex align="center">
                        {child.icon && (
                          <Box mr={8}>
                            {pathname === child.link ? (
                              <child.activeIcon className={classes.linkIcon} stroke={1.5} />
                            ) : (
                              <child.icon className={classes.linkIcon} stroke={1.5} />
                            )}
                          </Box>
                        )}
                        {child.label}
                      </Flex>
                    </Link>
                  ));
                })()}
              </div>
            </Collapse>
          )}



        </Box>
      );
    });
  }

  return (
    <Navbar
      width={{ base: menuType ? 56 : 204 }}
      className="sidebar-navbar"
      ref={navRef}
      onMouseEnter={handleNavMouseEnter}
      onMouseLeave={handleNavMouseLeave}
    >
      <Navbar.Section
        grow
        className="sidebar-nav-section"
        data-menu-type={menuType ? "true" : "false"}
        data-nav-expanded={navExpandedChrome ? "true" : "false"}
        py="xs"
        bg={"#F8F8FB"}
      >
        <CreateNewModal
          opened={modalOpened["idea"]}
          setOpened={(x) =>
            setModalOpened((prevState) => ({
              ...prevState,
              idea: x,
            }))
          }
        />
        <Box className="sidebar-nav-logo-row">
          <Link to={"/"} style={{ lineHeight: 0, display: "block" }}>
            <Box className="sidebar-nav-logo-outer">
              <Box className="sidebar-nav-logo-inner">
                <ExploreBagelSparkleIcon variant="insights" showSparkles={false} />
              </Box>
            </Box>
          </Link>
        </Box>
        {menuType && canCreateIdeas && (
          <Box className="sidebar-nav-add-row">
            <UnstyledButton
              className="sidebar-nav-add-btn"
              onClick={() => {
                setModalOpened((prevState) => ({
                  ...prevState,
                  idea: !modalOpened["idea"],
                }));
              }}
            >
              <Group noWrap mx="md">
                <Box className="sidebar-nav-plus-box">
                  <PlusIcon />
                </Box>
                <Text
                  fz="14px"
                  fw={500}
                  color="#5C5CEB"
                  className={sidebarNavAddTextClass(navExpandedChrome)}
                  lineClamp={1}
                >
                    Add New Idea{" "}
                </Text>
              </Group>
            </UnstyledButton>
          </Box>
        )}
        <Box className="sidebar-nav-main-scroll">
          {links}
        </Box>
        <Box className="sidebar-nav-footer-stack">
          {menuType && (
            <>
              <UnstyledButton
                type="button"
                className={sidebarFooterTargetClass(navExpandedChrome)}
                aria-label="Search ideas"
                aria-keyshortcuts="Control+K Meta+K"
                aria-expanded={searchPopoverOpened}
                onClick={() => setSearchPopoverOpened((o) => !o)}
              >
                <Flex align="center" w="100%">
                  <Box className="sidebar-footer-icon-slot">
                    <IconSearch color="#4B4BE8" size={20} stroke={2} />
                  </Box>
                  <Text
                    ml="md"
                    fz="14px"
                    mb="1px"
                    lineClamp={1}
                    className={sidebarFooterLabelClass(navExpandedChrome)}
                  >
                    Search
                  </Text>
                </Flex>
              </UnstyledButton>
              {searchPopoverOpened &&
                createPortal(
                  <Box
                    ref={searchSpotlightRef}
                    className="sidebar-search-spotlight"
                  >
                    <Search
                      query={ideaQuery}
                      setQuery={setIdeaQuery}
                      variant="sidebar"
                      onClose={() => {
                        setSearchPopoverOpened(false);
                        clearNavCollapseTimer();
                        setHovered(false);
                      }}
                    />
                  </Box>,
                  document.body
                )}
              <Tooltip
                label={"What's New"}
                disabled={navExpandedChrome}
                opened={tooltipOpened}
                onMouseEnter={() => setTooltipOpened(true)}
                onMouseLeave={() => setTooltipOpened(false)}
              >
                <UnstyledButton
                  className={sidebarFooterTargetClass(navExpandedChrome)}
                  onClick={() => {
                    handleCheckOut(true);
                  }}
                >
                  <Flex align="center" w="100%">
                    <Box className="sidebar-footer-icon-slot">
                      {isBannerRead
                        ? <MegaPhone fill="#4B4BE8" />
                        : (
                          <Indicator size={10} color="#FF9499" inline>
                            <MegaPhone fill="#4B4BE8" />
                          </Indicator>
                        )}
                    </Box>
                    <Text
                      ml="md"
                      fz="14px"
                      mb="1px"
                      lineClamp={1}
                      className={sidebarFooterLabelClass(navExpandedChrome)}
                    >
                      What&apos;s new
                    </Text>
                  </Flex>
                </UnstyledButton>
              </Tooltip>
            </>
          )}
          {menuType && <NotificationsDropdown sidebarExpanded={sidebarFooterExpanded} />}
          {isSystemAdmin && menuType && <InviteMembersDropdown sidebarExpanded={navExpandedChrome} /> }
          <ProfileMenu sidebarExpanded={sidebarFooterExpanded} onOpenChange={setProfileMenuOpened} />
        </Box>
      </Navbar.Section>
    </Navbar>
  );
}
